import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import TierBadge from '../components/TierBadge.jsx'
import { commands as page } from '../content.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The command reference. A PAGE TYPE for long, generated, searchable content —
// the design brief's words for it are "a long, generated, searchable list of
// commands with names, descriptions, arguments and examples… read on a phone
// mid-conversation, and its content is machine-generated rather than
// hand-tuned."
//
// 🔴 NOT ONE COMMAND NAME IS WRITTEN IN THIS FILE, and none may be. Everything
// below reads content/commands.json, which the generator builds from
// content-src/command-catalog.json — the exporter's output, walked from the
// commands the bot actually registered. That is the whole property: after P6-14
// added autocomplete, `/help hoops_bs` started saying "autocompletes" with no
// edit to cogs/help.py, and this page inherits the same behaviour. A hardcoded
// list here would end that on the first merge.
//
// Three decisions this file makes, in order of how easy they are to undo by
// accident:
//
//   1. NO ENTRY ANIMATION ON THE LIST. The system's stagger caps at six items
//      and this list is an order of magnitude longer; worse, it re-renders on
//      every keystroke, so an entry animation would re-run once per row per
//      search. The title block is static too, for the same reason a spinner
//      would be wrong here: the data is in the DOM at first paint. Motion in
//      this system is for arrival, and a filtered list has not arrived.
//   2. NOTHING IS COLLAPSED. Options and examples are visible without a tap.
//      The reader is mid-conversation on a phone and the thing they came for
//      is usually an argument name, so putting arguments behind a disclosure
//      would hide the answer. The explainer collapses detail because its detail
//      is optional; this page's is the point.
//   3. SEARCH LIVES IN THE URL. ?q= and ?feature= are replace-navigations, so
//      the back button leaves the page rather than replaying every keystroke,
//      and a filtered view is something you can send to somebody.

/** Terms a command must match ALL of. Tolerant of the separators nobody
 * remembers: `/movie_pick`, `movie pick` and `moviepick` are the same query,
 * matched against the three spellings the generator put in the haystack. */
function parseQuery(raw) {
  return (raw ?? '')
    .toLowerCase()
    .split(/\s+/)
    .map((term) => term.replace(/^\//, '').trim())
    .filter(Boolean)
}

function matches(command, terms) {
  return terms.every((term) => command.haystack.includes(term))
}

function Parameter({ parameter }) {
  // "(string, required · autocompletes)" — deliberately the same shape /help
  // renders in Discord, so the two surfaces read as one product. Plain words,
  // no icons: this is the part of the screen that is about what the bot will
  // ask you for.
  const bits = [parameter.type, parameter.required ? 'required' : 'optional']
  if (parameter.autocompletes) bits.push('suggests answers')

  return (
    <div className="d2d-param">
      <dt>
        <code className="d2d-param__name">{parameter.name}</code>{' '}
        <span className="d2d-param__meta d2d-micro">{bits.join(' · ')}</span>
      </dt>
      <dd>
        {parameter.description}
        {parameter.choices.length > 0 ? (
          <span className="d2d-param__choices"> Choose from: {parameter.choices.join(', ')}.</span>
        ) : null}
      </dd>
    </div>
  )
}

function Command({ command }) {
  return (
    <li className="d2d-cmd" data-testid={`command-${command.slug}`}>
      <h3 className="d2d-cmd__head">
        {/* The name is its own permalink. One element instead of a separate
            "#" affordance: it is already the widest tap target in the row, and
            its accessible name is the command, which is what somebody copying
            a link is trying to point at. */}
        <a className="d2d-cmd__anchor" id={command.slug} href={`#${command.slug}`}>
          {command.invocation}
        </a>
        {command.adminOnly ? (
          <span className="d2d-tag d2d-micro" data-testid="admin-tag">
            Admin
          </span>
        ) : null}
      </h3>

      <p className="d2d-cmd__desc">{command.description}</p>

      {command.parameters.length > 0 ? (
        <dl className="d2d-params">
          {command.parameters.map((parameter) => (
            <Parameter key={parameter.name} parameter={parameter} />
          ))}
        </dl>
      ) : null}

      {command.example ? (
        <p className="d2d-cmd__example">
          <span className="d2d-micro d2d-cmd__examplelabel">Example</span>{' '}
          <code>{command.example}</code>
        </p>
      ) : null}
    </li>
  )
}

export default function Commands({ section }) {
  useDocumentTitle(page.title)

  const [searchParams, setSearchParams] = useSearchParams()
  const inputRef = useRef(null)
  const resultsRef = useRef(null)
  const settled = useRef(false)

  const query = searchParams.get('q') ?? ''
  const requested = searchParams.get('feature') ?? ''
  // An unrecognised ?feature= is ignored rather than rendered as "no results".
  // A stale link should show the reader a page, not an empty one with no
  // explanation of what it filtered on.
  const feature = page.features.some((row) => row.key === requested) ? requested : ''

  const terms = useMemo(() => parseQuery(query), [query])

  const visible = useMemo(
    () =>
      page.commands.filter(
        (command) =>
          (feature === '' || command.feature === feature) && matches(command, terms),
      ),
    [feature, terms],
  )

  const groups = useMemo(
    () =>
      page.features
        .map((row) => ({ ...row, items: visible.filter((c) => c.feature === row.key) }))
        .filter((row) => row.items.length > 0),
    [visible],
  )

  function update(next) {
    const params = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(next)) {
      if (value) params.set(key, value)
      else params.delete(key)
    }
    // replace, not push: a search box that writes one history entry per
    // keystroke makes the back button useless on the page it is on.
    setSearchParams(params, { replace: true })
  }

  // A link to /commands#sickos-pick has to land on the command. The browser
  // resolves a fragment before React has rendered anything, so on a cold load
  // nothing has scrolled by the time the list exists — this is the part the
  // browser cannot do for a client-rendered page. Guarded because jsdom does
  // not implement scrollIntoView, and a test environment throwing here would
  // be a failure with nothing wrong with it.
  useEffect(() => {
    const raw = window.location.hash.replace(/^#/, '')
    if (!raw) return
    // decodeURIComponent throws on a malformed escape ("#100%"), and an
    // uncaught throw in an effect unmounts the tree — i.e. a junk fragment
    // somebody pasted would blank the page rather than just not scroll.
    let slug
    try {
      slug = decodeURIComponent(raw)
    } catch {
      slug = raw
    }
    const target = document.getElementById(slug)
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView()
    }
  }, [])

  // 🔴 Filtering from deep in the page used to leave you in the footer.
  // Measured in a real browser at 375px: scrolled to the bottom of the
  // unfiltered list and tapping a one-command feature chip, the page shrank
  // from 15,920px to 1,619px, the browser clamped the scroll position, and the
  // single result ended up 107px ABOVE the viewport — the reader saw the
  // footer and concluded the filter had emptied the page. Every test passed.
  //
  // Deliberately only when the results have already scrolled off the top: at
  // the top of the page this is a no-op, so typing does not yank the view.
  // Skipped on the first run so a /commands?q=…#slug link scrolls to the
  // command rather than fighting the anchor effect above.
  useEffect(() => {
    if (!settled.current) {
      settled.current = true
      return
    }
    const results = resultsRef.current
    if (!results || typeof results.scrollIntoView !== 'function') return
    if (results.getBoundingClientRect().top < 0) results.scrollIntoView()
  }, [query, feature])

  const filtered = terms.length > 0 || feature !== ''
  const count = filtered
    ? `${visible.length} of ${page.counts.commands} commands`
    : `${page.counts.commands} commands`

  return (
    <article className="d2d-page">
      <header className="d2d-titleblock">
        <p className="d2d-eyebrow d2d-micro">{page.eyebrow}</p>
        <h1 className="d2d-titleblock__headline">{page.title}</h1>
        <p className="d2d-titleblock__lede">{page.intro}</p>
        <hr className="d2d-rule" />
        <p className="d2d-titleblock__meta d2d-micro">
          {page.meta} · <TierBadge tier={section.tier} />
        </p>
      </header>

      <div className="d2d-cmdbar">
        <div className="d2d-search">
          <label className="d2d-sr-only" htmlFor="command-search">
            {page.search.label}
          </label>
          <input
            className="d2d-search__input"
            id="command-search"
            type="search"
            autoComplete="off"
            spellCheck="false"
            placeholder={page.search.placeholder}
            value={query}
            ref={inputRef}
            onChange={(event) => update({ q: event.target.value })}
          />
          {query ? (
            <button
              className="d2d-search__clear"
              type="button"
              onClick={() => {
                update({ q: '' })
                // The button unmounts on clear, so focus would otherwise fall
                // back to <body> and a keyboard user would lose their place.
                inputRef.current?.focus()
              }}
            >
              {page.search.clear}
            </button>
          ) : null}
        </div>

        {/* Buttons rather than links: this filters what is on screen, it does
            not navigate. aria-pressed is what tells a screen reader which one
            is on, since on a phone the row scrolls and the active chip may be
            off-screen. */}
        <div className="d2d-chiprow" role="group" aria-label="Filter by feature">
          <button
            className="d2d-chip"
            type="button"
            aria-pressed={feature === ''}
            onClick={() => update({ feature: '' })}
          >
            {page.allFeaturesLabel} <span className="d2d-chip__count">{page.counts.commands}</span>
          </button>
          {page.features.map((row) => (
            <button
              key={row.key}
              className="d2d-chip"
              type="button"
              aria-pressed={feature === row.key}
              onClick={() => update({ feature: feature === row.key ? '' : row.key })}
            >
              {row.label} <span className="d2d-chip__count">{row.count}</span>
            </button>
          ))}
        </div>

        <p className="d2d-cmdcount d2d-micro" role="status" data-testid="command-count">
          {count}
        </p>
      </div>

      <div className="d2d-results" ref={resultsRef}>
        {groups.length === 0 ? (
          <p className="d2d-notice" data-testid="commands-empty">
            {page.emptyState}
          </p>
        ) : (
          groups.map((group) => (
            <section
              className="d2d-cmdgroup"
              key={group.key}
              aria-labelledby={`feature-${group.key}`}
            >
              <h2 className="d2d-cmdgroup__head" id={`feature-${group.key}`}>
                {group.label}{' '}
                <span className="d2d-cmdgroup__count d2d-micro">{group.items.length}</span>
              </h2>
              {group.blurb ? <p className="d2d-cmdgroup__blurb">{group.blurb}</p> : null}
              <ul className="d2d-cmdlist">
                {group.items.map((command) => (
                  <Command key={command.slug} command={command} />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>

      <details className="d2d-disclosure d2d-cmdnotes">
        <summary>
          {page.notes.summary}
          <span className="d2d-disclosure__plus" aria-hidden="true">
            +
          </span>
        </summary>
        <div className="d2d-disclosure__panel">
          <dl>
            {page.notes.items.map(([term, description]) => (
              <div key={term}>
                <dt>
                  <strong>{term}</strong>
                </dt>
                <dd>{description}</dd>
              </div>
            ))}
          </dl>
        </div>
      </details>
    </article>
  )
}
